import { useMutation, useQuery } from '@apollo/client';
import { off, onValue, ref } from 'firebase/database';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { AI_REPLY, SEND_MESSAGE, UPDATE_CHAT_CONVERSATION } from '../../apollo-client/graphql/chat-conversation/chat-conversation.mutation';
import { GET_CHAT_CONVERSATIONS } from "../../apollo-client/graphql/chat-conversation/chat-conversation.query";
import { GET_CUSTOMER_DETAILS } from '../../apollo-client/graphql/customer/customer.query';
import { GET_WIDGET_CONFIGURATION_CHAT_INTERFACE } from "../../apollo-client/graphql/widget-configuration/widget-configuration.query";
import { getRealTimeFirebaseDB } from '../../lib/firebase';
import { cn } from '../../lib/utils';
import { changeHTMLAttribute, convertHexToHSL } from "../../utils/change-html-attribute";
import RoundedSpinner from '../common/rounded-spinner';
import ChatbotBottom from './chatbot-bottom';
import ChatbotHeader from './chatbot-header';
import ChatbotInitAction from './chatbot-init-action';
import Home from './home';
import LeadForm from './lead-form';
import { MessageList } from './message-list';
import { isMobile } from 'react-device-detect';

const Chatbot = () => {
  
  // ALL STATE
  const [isOpen, setIsOpen] = useState(false);
  const [isShowLeadForm, setIsShowLeadForm] = useState(true);
  const [chatConfigurationStyle, setChatConfigurationStyle] = useState(false);
  const [clickFromIcon, setClickFromIcon] = useState(false);
  const [leadConfig, setLeadConfig] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [skiableMessagesCount, setSkiableMessagesCount] = useState(0);
  const [conversation, setConversation] = useState([]);
  const [conversationCount, setConversationCount] = useState(0);
  const [newMessage, setNewMessage] = useState("");
  const [iframeInitActionHeight, setIframeInitActionHeight] = useState("");
  const [iframeHeight, setIframeHeight] = useState("");
  const [homeHeight, setHomeHeight] = useState("");
  const [leadFormHeight, setLeadFormHeight] = useState("");
  const [showFaqs, setShowFaqs] = useState(true);
  const [showHome, setShowHome] = useState(true);

  // LATEST MESSAGE SCROLL REF
  const messageListRef = useRef(null);
  const conversationRef = useRef(null);

  // GET BUSINESS ID FROM URL
  const queryString = window.location.search;
  const queryParams = new URLSearchParams(queryString);
  const businessId = queryParams.get('businessid');


  // GET CUSTOMER ID
  const [customerId, setCustomerId] = useState(() => localStorage.getItem(businessId));

  // Function to update customerId and set it to the cookie
  const updateCustomerId = (newCustomerId) => {
    localStorage.setItem(businessId, newCustomerId);
    setCustomerId(newCustomerId);
  };

  // MUTATION
  const [sendMessageMutation, { loading: sendMessageLoading }] = useMutation(SEND_MESSAGE);
  const [aiReplyMutation, { loading: aiReplyLoading }] = useMutation(AI_REPLY);
  const [updateChatConversation] = useMutation(UPDATE_CHAT_CONVERSATION);

  // GET WIDGET CONFIGURATION CHAT INTERFACE
  const { data, loading: chatConfigurationStyleLoading, error } = useQuery(GET_WIDGET_CONFIGURATION_CHAT_INTERFACE, {
    variables: { businessIdFromChatbot: businessId },
    fetchPolicy: "cache-and-network",
    skip: !businessId
  })

  // GET CUSTOMER DETAILS
  const { data: customerDetails } = useQuery(GET_CUSTOMER_DETAILS, {
    fetchPolicy: "cache-and-network",
    variables: { id: customerId, companyId: businessId },
    skip: !customerId || !businessId
  })

  // GET CONVERSATIONS LIST
  const { loading: chatConversationsLoading, refetch: chatConversationRefetch } = useQuery(GET_CHAT_CONVERSATIONS, {
    fetchPolicy: "cache-and-network",
    variables: {
      input: {
        customer: customerId,
        limit: 20,
        skip: skiableMessagesCount,
        companyId: businessId
      }
    },
    skip: (!customerId && isShowLeadForm),
    onCompleted: (data) => {
      if (data?.getChatConversationsByCustomerId) {
        const { data: conversations, count } = data.getChatConversationsByCustomerId;
        const reversedData = [...conversations].reverse();

        setConversation((prevConversation) => {
          const updatePreData = prevConversation?.filter((con) => customerId === con?.customer) || [];
          const mergedConversations = [...reversedData, ...updatePreData];

          // Filter out duplicate conversations based on _id
          const uniqueConversations = mergedConversations.reduce((acc, current) => {
            if (!acc.some((con) => con._id === current._id)) {
              acc.push(current);
            }
            return acc;
          }, []);

          return uniqueConversations;
        });

        setConversationCount(count);
      }
    }
  });

  useEffect(() => {
    conversationRef.current = conversation;
  }, [conversation])

  useEffect(() => {
    if (data?.getWidgetConfigurationForChatbot) {
      setChatConfigurationStyle(data?.getWidgetConfigurationForChatbot?.chatbot);
      setLeadConfig(data?.getWidgetConfigurationForChatbot?.leads);
      const buttonColor = data?.getWidgetConfigurationForChatbot?.chatbot?.buttonColor || "#000000";
      changeHTMLAttribute("mode", "light");
      changeHTMLAttribute("theme", buttonColor);
      const root = document.documentElement;
      if (root && buttonColor) {
        setTimeout(() => {
          const hslColor = convertHexToHSL(buttonColor);
          if (hslColor) {
            root.style.setProperty("--primary", hslColor);
          }
        }, 100); // Delay for async updates
      }
    }
  }, [data?.getWidgetConfigurationForChatbot])

  useEffect(() => {
    if (customerDetails?.getCustomerDetails) {
      setCustomer(customerDetails?.getCustomerDetails);
    }
  }, [customerDetails?.getCustomerDetails])

  // LISTEN LIVE MESSAGES FROM FIREBASE
  useEffect(() => {
    let messagesRef;
    if (businessId && customerId) {
      messagesRef = ref(getRealTimeFirebaseDB, `messages/${businessId?.toString()}/${customerId?.toString()}`);

      onValue(messagesRef, (snapshot) => {
        const data = snapshot.val();

        // PUSH NEW MESSAGE TO SELECTED USER'S CONVERSATION
        if (data?.customer === customerId) {
          setConversation((prevConversation) => {
            const conversationArray = Array.isArray(prevConversation) ? prevConversation : [];

            return conversationArray.find((d) => d?._id === data?._id)
              ? conversationArray
              : [...conversationArray, data];
          });
          setTimeout(() => {
            if (messageListRef.current) {
              const messageList = messageListRef.current;
              messageList.scrollTop = messageList.scrollHeight;
            }
          }, 500);
        }
      });
    }
    return () => {
      if (messagesRef) {
        off(messagesRef, "value");
      }
    };
  }, [businessId, customerId]);

  // LISTEN LIVE USER FEEDBACK MESSAGES FROM FIREBASE
  useEffect(() => {
    let newFeedBackMessagesRef;
    if (businessId && customerId) {
      newFeedBackMessagesRef = ref(getRealTimeFirebaseDB, `feedback/${businessId}/${customerId}`);

      onValue(newFeedBackMessagesRef, (snapshot) => {
        const feedBackData = snapshot.val();
        if (feedBackData?.from === "operator") {
          setConversation((prevState) =>
            prevState?.map((con) =>
              con?._id === feedBackData?.messageId
                ? { ...con, isLiked: feedBackData?.isLiked }
                : con
            )
          );
        }
      });
    }
    return () => {
      if (newFeedBackMessagesRef) {
        off(newFeedBackMessagesRef, "value");
      }
    };
  }, [businessId, customerId]);

  useEffect(() => {
    try {
      if (!iframeInitActionHeight) return;
      const message = {
        isOpen,
        iframeInitActionHeight: `${iframeInitActionHeight}px`,
        ...(!showFaqs && { chatbotTriggerIconWidth: "52px" })
      };
      window.parent.postMessage(message, '*');
    } catch (error) {
      console.error('Error Sending message to parent:', error);
    }
  }, [isOpen, iframeInitActionHeight, showFaqs]);

  useEffect(() => {
    window?.addEventListener('message', (event) => {
      if (event.data.type === 'UPDATE_HEIGHT') {
        const reducedHeight = event.data.messageScrollHeight;
        const homeHeight = event.data.homeHeight;
        const leadFormHeight = event.data.leadFormHeight;
        if (isOpen) {
          setIframeHeight(reducedHeight);
          setHomeHeight(homeHeight);
          setLeadFormHeight(leadFormHeight);
        };
      }
    });
  }, [isOpen])

  const handleSendMessage = async (faq) => {
    try {
      setShowHome(false);
      if (aiReplyLoading || sendMessageLoading || (!faq && !newMessage)) return;

      // SEND MESSAGE API
      const sendMessageInput = { customerId, companyId: businessId, message: faq?.question || faq?.message || newMessage?.trim(), anonymousUser: customerId ? false : true }// CUSTOMER IS ALREADY EXIST FROM LEAD FROM OR CREATE AS ANONYMOUS
      setNewMessage("");
      const res = await sendMessageMutation({ variables: { input: sendMessageInput } });
      if (!customerId) {
        updateCustomerId(res?.data?.sendNewMessageFromWebChatbot?.customer);  // This will update both the state and the cookie
      }
      setTimeout(() => {
        const messageList = messageListRef.current;
        messageList.scrollTop = messageList.scrollHeight;
      }, 500);

      // AI REPLY API
      const AiReplyInput = { customerId: res?.data?.sendNewMessageFromWebChatbot?.customer, companyId: businessId, faqId: faq?.id, messageId: res?.data?.sendNewMessageFromWebChatbot?._id }
      await aiReplyMutation({ variables: { input: AiReplyInput } });
      setTimeout(() => {
        const messageList = messageListRef.current;
        messageList.scrollTop = messageList.scrollHeight;
      }, 500);

    } catch (err) {
      const lastMessage = conversationRef.current?.[conversationRef.current.length - 1];
      if (lastMessage?._id) {
        await updateChatConversation({
          variables: { input: { messageId: lastMessage?._id, isFailed: true } },
        });
      }
      if (lastMessage) {
        lastMessage.isFailed = true;
        setConversation([...conversationRef.current]);
      }
      setTimeout(() => {
        if (messageListRef.current) {
          const messageList = messageListRef.current;
          messageList.scrollTop = messageList.scrollHeight;
        }
      }, 10);
    }
  }

  const sendMessageEventToParent = (isCallFromHideFaq) => {
    const iFrameHei = document.getElementById("chatbotInitAction").clientHeight;
    const message = {
      isOpen,
      iframeInitActionHeight: `${iFrameHei}px`,
      ...((isCallFromHideFaq || !showFaqs) && { chatbotTriggerIconWidth: "52px" })
    };
    window.parent.postMessage(message, '*');
    setIframeInitActionHeight(iFrameHei);
  }

  if (error) return error?.message;
  if (chatConfigurationStyleLoading) return <RoundedSpinner />;

  return (
    <>
      <AnimatePresence mode="wait">
        {isOpen && (
          <motion.div
            initial={{
              y: 20,
              opacity: 0,
            }}
            animate={{
              y: 0,
              opacity: 1,
              transition: {
                duration: 0.5,
                ease: [0.25, 1, 0.5, 1],
              },
            }}
            exit={{
              y: 20,
              opacity: 0,
              transition: {
                duration: 0.5,
                ease: [0.4, 0, 0.5, 1],
              },
            }}
            className={cn("rounded-lg w-full h-full bg-primary mb-5 border-none shadow-[0px_0px_11px_0px_rgba(0,_0,_0,_0.15)]", showHome && "bg-white", isMobile && "mb-0 rounded-none")}
          >
            {
              showHome ?
                <Home showHome={showHome} setShowHome={setShowHome} chatConfigurationStyle={chatConfigurationStyle} handleSendMessage={handleSendMessage} homeHeight={homeHeight} setIsOpen={setIsOpen} /> :
                <div>
                  <ChatbotHeader
                    chatConfigurationStyle={chatConfigurationStyle}
                    isOpen={isOpen}
                    businessId={businessId}
                    setShowHome={setShowHome}
                    setIsOpen={setIsOpen}
                  />
                  {(isShowLeadForm && leadConfig?.enableOnSite && !customerId && clickFromIcon) ?
                    <LeadForm
                      isShowLeadForm={isShowLeadForm}
                      setIsShowLeadForm={setIsShowLeadForm}
                      leadConfig={leadConfig}
                      businessId={businessId}
                      chatConversationRefetch={chatConversationRefetch}
                      updateCustomerId={updateCustomerId}
                      leadFormHeight={leadFormHeight}
                    /> :
                    <>
                      <MessageList
                        messages={conversation}
                        count={conversationCount}
                        loading={chatConversationsLoading}
                        setSkiableMessagesCount={setSkiableMessagesCount}
                        messageListRef={messageListRef}
                        customer={customer}
                        chatConfigurationStyle={chatConfigurationStyle}
                        aiReplyLoading={aiReplyLoading}
                        handleSendMessage={handleSendMessage}
                        iframeHeight={iframeHeight}
                      />
                      <ChatbotBottom
                        chatConfigurationStyle={chatConfigurationStyle}
                        newMessage={newMessage}
                        setNewMessage={setNewMessage}
                        sendMessageLoading={sendMessageLoading}
                        handleSendMessage={handleSendMessage}
                        aiReplyLoading={aiReplyLoading}
                      />
                    </>
                  }
                </div>
            }
          </motion.div>
        )}
      </AnimatePresence>
      {(isMobile ? !isOpen : true) &&
        <ChatbotInitAction
          chatConfigurationStyle={chatConfigurationStyle}
          clickFromIcon={clickFromIcon}
          setClickFromIcon={setClickFromIcon}
          handleSendMessage={handleSendMessage}
          sendMessageEventToParent={sendMessageEventToParent}
          showFaqs={showFaqs}
          setShowFaqs={setShowFaqs}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
      }
    </>
  );
};

export default Chatbot;
